import _regeneratorRuntime from "/opt/build/repo/node_modules/@babel/runtime/regenerator";
import "regenerator-runtime/runtime";
import _asyncToGenerator from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";

/**
 * @license
 * Copyright 2018 Google Inc. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { whereImpl } from '../backends/where_impl';
import { ENGINE } from '../engine';
import { SelectV2 } from '../kernel_names';
import { convertToTensor } from '../tensor_util_env';
import { assert, assertShapesMatch } from '../util';
import { assertAndGetBroadcastShape } from './broadcast_util';
import { op } from './operation';
import { zerosLike } from './tensor_ops';
/**
 * Returns the truth value of `NOT x` element-wise.
 *
 * ```js
 * const a = tf.tensor1d([false, true], 'bool');
 *
 * a.logicalNot().print();
 * ```
 *
 * @param x The input tensor. Must be of dtype 'bool'.
 */

/** @doc {heading: 'Operations', subheading: 'Logical'} */

function logicalNot_(x) {
  var $x = convertToTensor(x, 'x', 'logicalNot', 'bool');
  return ENGINE.runKernelFunc(function (backend) {
    return backend.logicalNot($x);
  }, {
    $x: $x
  });
}
/**
 * Returns the truth value of `a AND b` element-wise. Supports broadcasting.
 *
 * ```js
 * const a = tf.tensor1d([false, false, true, true], 'bool');
 * const b = tf.tensor1d([false, true, false, true], 'bool');
 *
 * a.logicalAnd(b).print();
 * ```
 *
 * @param a The first input tensor. Must be of dtype bool.
 * @param b The second input tensor. Must be of dtype bool.
 */

/** @doc {heading: 'Operations', subheading: 'Logical'} */


function logicalAnd_(a, b) {
  var $a = convertToTensor(a, 'a', 'logicalAnd', 'bool');
  var $b = convertToTensor(b, 'b', 'logicalAnd', 'bool');
  assertAndGetBroadcastShape($a.shape, $b.shape);
  return ENGINE.runKernelFunc(function (backend) {
    return backend.logicalAnd($a, $b);
  }, {
    a: $a,
    b: $b
  }, null
  /* grad */
  , 'LogicalAnd');
}
/**
 * Returns the truth value of `a OR b` element-wise. Supports broadcasting.
 *
 * ```js
 * const a = tf.tensor1d([false, false, true, true], 'bool');
 * const b = tf.tensor1d([false, true, false, true], 'bool');
 *
 * a.logicalOr(b).print();
 * ```
 * @param a The first input tensor. Must be of dtype bool.
 * @param b The second input tensor. Must be of dtype bool.
 */

/** @doc {heading: 'Operations', subheading: 'Logical'} */


function logicalOr_(a, b) {
  var $a = convertToTensor(a, 'a', 'logicalOr', 'bool');
  var $b = convertToTensor(b, 'b', 'logicalOr', 'bool');
  assertAndGetBroadcastShape($a.shape, $b.shape);
  return ENGINE.runKernelFunc(function (backend) {
    return backend.logicalOr($a, $b);
  }, {
    $a: $a,
    $b: $b
  });
}
/**
 * Returns the truth value of `a XOR b` element-wise. Supports broadcasting.
 *
 * ```js
 * const a = tf.tensor1d([false, false, true, true], 'bool');
 * const b = tf.tensor1d([false, true, false, true], 'bool');
 *
 * a.logicalXor(b).print();
 * ```
 *
 * @param a The first input tensor. Must be of dtype bool.
 * @param b The second input tensor. Must be of dtype bool.
 */

/** @doc {heading: 'Operations', subheading: 'Logical'} */


function logicalXor_(a, b) {
  var $a = convertToTensor(a, 'a', 'logicalXor', 'bool');
  var $b = convertToTensor(b, 'b', 'logicalXor', 'bool');
  assertAndGetBroadcastShape($a.shape, $b.shape); // x ^ y = (x | y) & ~(x & y)

  return logicalOr(a, b).logicalAnd(logicalAnd(a, b).logicalNot());
}
/**
 * Returns the elements, either `a` or `b` depending on the `condition`.
 *
 * If the condition is true, select from `a`, otherwise select from `b`.
 *
 * ```js
 * const cond = tf.tensor1d([false, false, true], 'bool');
 * const a = tf.tensor1d([1 , 2, 3]);
 * const b = tf.tensor1d([-1, -2, -3]);
 *
 * a.where(cond, b).print();
 * ```
 *
 * @param condition The input condition. Must be of dtype bool.
 * @param a If `condition` is rank 1, `a` may have a higher rank but
 *     its first dimension must match the size of `condition`.
 * @param b A tensor with the same shape and type as `a`.
 */

/** @doc {heading: 'Operations', subheading: 'Logical'} */


function where_(condition, a, b) {
  var $a = convertToTensor(a, 'a', 'where');
  var $b = convertToTensor(b, 'b', 'where');
  var $condition = convertToTensor(condition, 'condition', 'where', 'bool');
  assertShapesMatch($a.shape, $b.shape, 'Error in where: ');

  if ($condition.rank === 1) {
    // If condition rank is 1, then the first dimension must match the size of
    // condition.
    assert($condition.shape[0] === $a.shape[0], function () {
      return 'The first dimension of `a` must match the size of `condition`.';
    });
  } else {
    // A must have the same shape as condition.
    assertShapesMatch($condition.shape, $b.shape, 'Error in where: ');
  } // TODO(julianoks): Return null for condition gradient
  // when backprop supports it.


  var grad = function grad(dy, saved) {
    var _saved = _slicedToArray(saved, 1),
        $condition = _saved[0];

    return {
      condition: function condition() {
        return zerosLike($condition).toFloat();
      },
      t: function t() {
        return dy.mul($condition.cast(dy.dtype));
      },
      e: function e() {
        return dy.mul($condition.logicalNot().cast(dy.dtype));
      }
    };
  };

  var inputs = {
    condition: $condition,
    t: $a,
    e: $b
  };
  return ENGINE.runKernelFunc(function (backend, save) {
    var res = backend.select($condition, $a, $b);
    save([$condition]);
    return res;
  }, inputs, grad, SelectV2);
}
/**
 * Returns the coordinates of true elements of condition.
 *
 * The coordinates are returned in a 2-D tensor where the first dimension (rows)
 * represents the number of true elements, and the second dimension (columns)
 * represents the coordinates of the true elements. Keep in mind, the shape of
 * the output tensor can vary depending on how many true values there are in
 * input. Indices are output in row-major order. The resulting tensor has the
 * shape `[numTrueElems, condition.rank]`.
 *
 * This is analogous to calling the python `tf.where(cond)` without an x or y.
 *
 * ```js
 * const cond = tf.tensor1d([false, false, true], 'bool');
 * const result = await tf.whereAsync(cond);
 * result.print();
 * ```
 */

/** @doc {heading: 'Operations', subheading: 'Logical'} */


function whereAsync_(_x) {
  return _whereAsync_.apply(this, arguments);
}

function _whereAsync_() {
  _whereAsync_ = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(condition) {
    var $condition, vals, res;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            $condition = convertToTensor(condition, 'condition', 'whereAsync', 'bool');
            _context.next = 3;
            return $condition.data();

          case 3:
            vals = _context.sent;
            res = whereImpl($condition.shape, vals);

            if (condition !== $condition) {
              $condition.dispose();
            }

            return _context.abrupt("return", res);

          case 7:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _whereAsync_.apply(this, arguments);
}

export var logicalAnd = op({
  logicalAnd_: logicalAnd_
});
export var logicalNot = op({
  logicalNot_: logicalNot_
});
export var logicalOr = op({
  logicalOr_: logicalOr_
});
export var logicalXor = op({
  logicalXor_: logicalXor_
});
export var where = op({
  where_: where_
});
export var whereAsync = whereAsync_;